import { ref, toRaw } from "vue"
import LocationHelper from "@/helper/LocationHelper";

import { Loader } from '@googlemaps/js-api-loader'; //https://www.npmjs.com/package/@googlemaps/js-api-loader
import Utility from "@/helper/Utility";

//#region type definitions

/**
 * @typedef {Object} MarkerConfig
 * @property {boolean=} useActiveIcon
 * @property {*} normalIcon
 * @property {*} activeIcon
 * @property {number} iconHeight
 * @property {number} iconWidth
 * @property {number} activeIconHeight
 * @property {number} activeIconWidth
 */

//#endregion type definitions

/**
 * 
 * @param {import("@vue/reactivity").Ref<HTMLDivElement>} elemMap 
 * @param {*} loaderConfig
 * @param {google.maps.MapOptions=} config 
 * @returns 
 */
export const MapsService = function (elemMap, loaderConfig, config = {}) {
    
    const defaultConfig = {
        zoom: 15,
        mapTypeId: "roadmap",
        disableDefaultUI: true,
        fullscreenControl: false,
    }

    const mapConfig = {
        ...defaultConfig,
        ...config
    }
    /**@type {import("@vue/reactivity").Ref<google>} */
    const googleApi = ref()
    /**@type {import("@vue/reactivity").Ref<google.maps.Map>} */
    const map = ref()
    const latLng = ref({
        lat: 0,
        lng: 0
    })
    /**@type {import("@vue/reactivity").Ref<google.maps.Marker[]>} */
    const markers = ref([])
    /**@type {import("@vue/reactivity").Ref<google.maps.Marker>} */
    const activeMarker = ref()
    /**@type {import("@vue/reactivity").Ref<google.maps.Marker>} */
    const pinMarker = ref()

    //#region methods
    const getCurrentLocation = function () {
        return new Promise((resolve) => {
            LocationHelper.getCurrentLocation().then((location) => {
                latLng.value = location
                resolve(latLng.value);
            }).catch((err) => {
                console.log(err);
                // 13.7248934,100.4926816 bangkok
                latLng.value = {
                    lat: 13.7248934,
                    lng: 100.4926816
                }
                resolve(latLng.value);
            });
        });
    }

    const initMaps = function () {
        const loader = new Loader(loaderConfig);

        return loader.load().then((google) => {
            googleApi.value = google;

            if (elemMap.value != null) {
                map.value = new google.maps.Map(elemMap.value, {
                    center: latLng.value,
                    ...mapConfig
                });
            }
        }).catch(e => {
            console.log(e);
            // do something
        });
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} targetPos 
     */
    const showDirection = function (targetPos) {
        let originLocation = `${latLng.value.lat},${latLng.value.lng}`;
        let destinationLocation = `${targetPos.lat},${targetPos.lng}`;

        const directionsService = new googleApi.value.maps.DirectionsService();
        const directionsRenderer = new googleApi.value.maps.DirectionsRenderer();
        directionsRenderer.setMap(map.value);

        return directionsService.route({
            origin: {
                query: originLocation,
            },
            destination: {
                query: destinationLocation,
            },
            travelMode: googleApi.value.maps.TravelMode.DRIVING,
        }).then((response) => {
            directionsRenderer.setDirections(response);
        })
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} position
     * @param {*} customIcon
     * @param {*} iconWidth
     * @param {*} iconHeight
     * @param {*} draggable
     */
    const pinCurrent = function (position = null, customIcon = null, iconWidth = null, iconHeight = null, draggable = false) {
        if (position == null) {
            position = latLng.value
        }
        let marker = new googleApi.value.maps.Marker({
            map: map.value,
            position: position,
            draggable: draggable,
        })
        if (customIcon != null) {
            setMarkerIcon(marker, customIcon, iconWidth, iconHeight)
        }
        if (draggable) {
            marker.addListener("dragend", () => {
                map.value.setCenter(marker.getPosition())
            })
        }
        pinMarker.value = marker
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} pos 
     */
    const panTo = function (pos) {
        map.value.panTo(pos);
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} pos 
     * @param {Function=} handler
     * @param {MarkerConfig} config
     */
    const addMarker = function (pos, handler = null, config = {}) {
        // console.log(markers)
        let marker = new googleApi.value.maps.Marker({
            map: map.value,
            position: pos,
            // icon: pinMap,
            optimized: true
        })

        if (config.normalIcon != null) {
            setMarkerIcon(marker, config.normalIcon, config.iconWidth, config.iconHeight)
        }

        if (Utility.isFunction(handler)) {
            marker.addListener('click', () => {
                if (config.useActiveIcon === true) {
                    // reset previous active marker icon
                    if (activeMarker.value != null) {
                        setMarkerIcon(toRaw(activeMarker.value), config.normalIcon, config.iconWidth, config.iconHeight)
                    }
                    setMarkerIcon(marker, config.activeIcon, config?.activeIconWidth ?? config.iconWidth, config?.activeIconHeight ?? config.iconHeight)
                }

                activeMarker.value = marker
                handler(marker)
            });
        }

        markers.value.push(marker)
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} targetPos 
     * @param {import("@/helper/LocationHelper").LatLng=} userPos 
     */
    const getDistanceInfo = function (targetPos, userPos = null) {
        // console.log(" call distance info")
        let originLocation = `${latLng.value.lat},${latLng.value.lng}`;
        if (userPos != null) {
            `${userPos.lat},${userPos.lng}`;
        }
        let destinationLocation = `${targetPos.lat},${targetPos.lng}`;

        const directionsService = new googleApi.value.maps.DirectionsService();
        return directionsService.route({
            origin: {
                query: originLocation,
            },
            destination: {
                query: destinationLocation,
            },
            provideRouteAlternatives: false,
            travelMode: googleApi.value.maps.TravelMode.DRIVING,
            unitSystem: googleApi.value.maps.UnitSystem.METRIC,
            drivingOptions: {
                departureTime: new Date(),
                trafficModel: 'pessimistic'
            },
        }).then((response) => {
            if (response.status == "OK") {
                let leg = response.routes[0].legs?.[0] ?? null
                if (leg != null) {
                    return leg
                }
            }

            return null
        })
    }

    /**
     * 
     * @param {import("@/helper/LocationHelper").LatLng} latLng 
     */
    const getAddressByGeolocation = function (latLng) {
        // console.log(latLng)
        let geocoder = new googleApi.value.maps.Geocoder;
        return geocoder.geocode({ location: latLng }).then((res) => {
            // console.log(res)
            let place_info = ""
            if (res?.results?.[0] != null) {
                place_info = res.results[0].formatted_address
            }
            return place_info
        }).catch(() => {
            return ""
        })
    }

    /**
     * 
     * @param {google.maps.Marker} marker 
     * @param {*} iconDataUrl
     */
    const setMarkerIcon = function (marker, iconDataUrl, width = null, height = null) {
        let icon = {
            url: iconDataUrl
        }
        if (width != null && height != null) {
            icon.scaledSize = new googleApi.value.maps.Size(width, height)
        }
        // console.log(marker)
        marker.setIcon(icon)
    }

    /**
     * clear all marker except current user location
     */
    const clearMarker = function () {
        markers.value.forEach((m) => {
            let marker = toRaw(m)
            marker.setMap(null)
        })
        markers.value = []
    }

    //#endregion

    return {
        latLng,
        pinMarker,
        initMaps,
        getCurrentLocation,
        pinCurrent,
        addMarker,
        panTo,
        clearMarker,
        showDirection,
        getDistanceInfo,
        getAddressByGeolocation
    }
}
/**
 * @see {@link https://www.geodatasource.com/developers/javascript} reference
 * @param {number} lat1 
 * @param {number} lon1 
 * @param {number} lat2 
 * @param {number} lon2 
 * @param {number} unit 
 * @returns 
 */
export const calculateDistance = function (lat1, lng1, lat2, lng2, unit = "K") {
    if ((lat1 == lat2) && (lng1 == lng2)) {
        return 0;
    }
    else {
        let radlat1 = Math.PI * lat1 / 180;
        let radlat2 = Math.PI * lat2 / 180;
        let theta = lng1 - lng2;
        let radtheta = Math.PI * theta / 180;
        let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI;
        dist = dist * 60 * 1.1515;
        if (unit == "K") { dist = dist * 1.609344 }
        if (unit == "N") { dist = dist * 0.8684 }
        return dist;
    }
}

export default MapsService